import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IOdbDetails,
  IOdbPort,
  IOdbSearchOnu,
  IOdbStatusSeries,
  IOnuStatusSegment,
  IOnuView,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmEventType, ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';

/** Icono y color del estado de la ONU, como los pinta SmartOLT en cada puerto. */
const STATUS_META: Record<string, { icon: string; color: string; label: string }> = {
  Online: { icon: 'pi-home', color: '#1fb325', label: 'Online' },
  'Power fail': { icon: 'pi-bolt', color: '#4c4b4b', label: 'Power fail' },
  LOS: { icon: 'pi-link', color: '#dc2626', label: 'LOS' },
  Offline: { icon: 'pi-home', color: '#4c4b4b', label: 'Offline' },
};
const UNKNOWN_STATUS = { icon: 'pi-question-circle', color: '#64748b', label: 'Desconocido' };

/** Lo que el splitter necesita de la ONU: desde cuál se lee y el id del splitter. */
export type OnuSplitterRef = Pick<IOnuView, 'onuId'> & {
  location: Pick<IOnuView['location'], 'odbId'>;
};

/** Colores de la barra de estado de 24 h de cada puerto. */
export const STATUS_BAND_COLORS: Record<string, string> = {
  Online: '#1fb325',
  LOS: '#dc2626',
  'Power fail': '#0064C8',
  Offline: '#4c4b4b',
  'Log in': '#eab308',
  Flapping: '#9333ea',
};

type Severity = 'good' | 'warning' | 'critical' | 'none';
const SIGNAL_META: Record<Severity, { color: string; label: string }> = {
  good: { color: '#1fb325', label: 'Señal buena' },
  warning: { color: 'darkorange', label: 'Señal en advertencia' },
  critical: { color: '#ef4444', label: 'Señal crítica' },
  none: { color: '#4c4b4b', label: 'Sin señal' },
};

/** Opciones de divisor de SmartOLT: 1:2 a 1:128 y "Otro". */
export const SPLITTER_PORT_CHOICES = [2, 4, 8, 16, 32, 64, 128];

type Selection = { onuId: string | null; port: number | null };
type StatusCategory = 'online' | 'offline' | 'unknown' | 'free';
type UsageCategory = 'good' | 'warning' | 'critical' | 'offline' | 'free';
type UsageFilter = { type: 'status' | 'signal'; value: string } | null;

export interface IOdbUsage {
  status: Record<StatusCategory, number>;
  signal: Record<UsageCategory, number>;
  /** vacíos: null si el número de puertos no está definido */
  free: number | null;
  utilization: number;
  statusGradient: string;
  signalGradient: string;
}

/** conic-gradient de un anillo, en el orden de SmartOLT. */
const ringGradient = (
  counts: Record<string, number>,
  total: number,
  colors: Record<string, string>,
): string => {
  if (!total) return 'conic-gradient(#d7dde6 0deg, #d7dde6 360deg)';
  let start = 0;
  const parts: string[] = [];
  Object.keys(colors).forEach((key) => {
    const value = counts[key] || 0;
    if (!value) return;
    const end = start + (value / total) * 360;
    parts.push(`${colors[key]} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`);
    start = end;
  });
  return `conic-gradient(${parts.join(', ')})`;
};

/**
 * Modal del splitter de SmartOLT: el splitter dibujado como el dispositivo, con cada puerto y la señal
 * de su ONU, el panel del puerto seleccionado (asignar, quitar y refrescar) y el resumen de estado y
 * señal con sus anillos.
 */
@Component({
  selector: 'app-onu-splitter',
  standalone: true,
  imports: [FormsModule, NgClass, NgTemplateOutlet, ButtonModule, MenuModule],
  templateUrl: './onu-splitter.component.html',
  styleUrl: './onu-splitter.component.scss',
})
export class OnuSplitterComponent implements OnInit, OnDestroy {
  @Input({ required: true }) view!: OnuSplitterRef;
  /** abre el detalle de otra ONU del splitter */
  @Output() openOnu = new EventEmitter<string>();
  /** se cambió algo del splitter: el modal de la ONU debe volver a leerse */
  @Output() changed = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  /** tarjeta compacta (el popover de SmartOLT) en vez del modal completo */
  @Input() card = false;
  /** "Ver splitter" de la tarjeta: abrir el modal completo */
  @Output() viewSplitter = new EventEmitter<void>();
  /** el splitter terminó de leerse (la tarjeta cambia de alto y se recoloca) */
  @Output() loaded = new EventEmitter<void>();

  public data: IOdbDetails | null = null;
  public loading = true;
  public error = '';
  public band = { rx1310: true, rx1490: true };
  public selected: Selection = { onuId: null, port: null };
  public busy = false;
  public refreshing = false;
  public refreshingAll = false;
  public filter: UsageFilter = null;
  public usage: IOdbUsage | null = null;

  // barra de estado de las últimas 24 h
  public series: IOdbStatusSeries = {};
  private seriesFrom = 0;
  private seriesTo = 0;

  // número de puertos
  public editingPorts = false;
  public portChoice: string = '';
  public portOther: number | null = null;

  // asignar una ONU a un puerto vacío
  public searchTerm = '';
  public searchResults: IOdbSearchOnu[] | null = null;
  public searching = false;
  private searchTimer?: ReturnType<typeof setTimeout>;
  /** ONU a la que se le está eligiendo puerto (clic en un puerto vacío) */
  public pickingOnu: IOdbPort | null = null;

  /** opciones del menú ⋮ del puerto pulsado */
  public menuItems: MenuItem[] = [];

  public readonly portChoices = SPLITTER_PORT_CHOICES;
  public readonly bands: { k: 'rx1310' | 'rx1490'; l: string; t: string }[] = [
    { k: 'rx1310', l: '1310 nm', t: 'Rx OLT' },
    { k: 'rx1490', l: '1490 nm', t: 'Rx ONU' },
  ];

  constructor(
    private readonly onuViewService: OnuViewService,
    private readonly toastService: ToastrService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  /** splitter abierto: se mantiene aunque la ONU del modal salga de él */
  private odbId = '';

  ngOnInit(): void {
    this.odbId = this.view.location.odbId;
    this.selected = { onuId: String(this.view.onuId), port: null };
    this.load();
  }
  ngOnDestroy(): void {
    // al cerrar el modal no se lanza la búsqueda que estaba esperando
    clearTimeout(this.searchTimer);
  }

  /**
   * load
   */
  public async load() {
    this.loading = !this.data;
    this.error = '';
    try {
      const response = await this.onuViewService.read<IOdbDetails>(this.view.onuId, 'odb_details', {
        odbId: this.odbId,
      });
      this.data = response.data;
      this.usage = this.buildUsage(this.data);
      // la tarjeta no dibuja la barra de 24 h
      if (!this.card) this.loadSeries();
    } catch (error) {
      this.error = 'No se pudo leer el splitter en SmartOLT.';
    } finally {
      this.loading = false;
      this.loaded.emit();
    }
  }

  /** Barra de estado de 24 h de las ONUs del splitter; si falla, los puertos quedan sin barra. */
  private async loadSeries() {
    const ids = (this.data?.ports ?? []).filter((p) => p.id && !p.free).map((p) => p.id);
    if (!ids.length) return;
    this.seriesTo = Math.floor(Date.now() / 1000);
    this.seriesFrom = this.seriesTo - 86400;
    try {
      const response = await this.onuViewService.read<IOdbStatusSeries>(
        this.view.onuId,
        'odb_status_series',
        { onu_ids: ids.join(','), from: this.seriesFrom, to: this.seriesTo },
      );
      this.series = response.data ?? {};
    } catch (error) {
      this.series = {};
    }
  }

  // ---- datos derivados ----
  public get assigned(): IOdbPort[] {
    return (this.data?.ports ?? []).filter((p) => !p.unassigned);
  }

  public get unassigned(): IOdbPort[] {
    return (this.data?.ports ?? []).filter((p) => p.unassigned);
  }

  public get totalPorts(): number {
    return this.data?.total_ports_defined === false ? 0 : this.data?.nr_of_ports ?? 0;
  }

  public get freePorts(): number {
    return (this.data?.ports ?? []).filter((p) => p.free && p.port).length;
  }

  /** Tamaño del dispositivo: hasta 8 puertos, hasta 16 o más (como SmartOLT). */
  public get shellSize(): 'compact' | 'medium' | 'wide' {
    const count = this.totalPorts || this.assigned.length;
    return count <= 8 ? 'compact' : count > 16 ? 'wide' : 'medium';
  }

  /** "Total de puertos sin definir • 81 en uso" o "16 puertos • 5 en uso • 11 vacíos". */
  public get summary(): string {
    if (!this.data) return '';
    const used = this.data.used_ports;
    return this.totalPorts
      ? `${this.totalPorts} puertos • ${used} en uso • ${Math.max(
          0,
          this.totalPorts - used,
        )} vacíos`
      : `Total de puertos sin definir • ${used} en uso`;
  }

  /** Misma línea de salud de SmartOLT; vacía si el splitter no tiene ONUs habilitadas. */
  public get healthLine(): string {
    const h = this.data?.health;
    if (!h || !(Number(h[0]) > 0)) return '';
    const avg =
      h[6] === null || h[6] === undefined || Number.isNaN(Number(h[6]))
        ? '-'
        : `${Number(h[6]).toFixed(1).replace('-', '−')} dBm`;
    return `Online ${h[1]} · LOS ${h[2]} · Power fail ${h[3]} · Offline ${h[4]} · Low signal ${h[5]} · Avg RX ${avg}`;
  }

  /** Número de puerto de una ONU (0 si no tiene). */
  public portNo(port: IOdbPort | null): number {
    return Number(port?.port) || 0;
  }

  public bandOn(band: 'rx1310' | 'rx1490'): boolean {
    return this.band[band];
  }

  public portLabel(port: IOdbPort): string {
    const number = this.portNo(port);
    if (!number) return 'Sin puerto';
    return `Puerto ${(this.data?.first_port_number ?? 1) + number - 1}`;
  }

  public statusMeta(port: IOdbPort) {
    return STATUS_META[port.status ?? ''] ?? UNKNOWN_STATUS;
  }

  public rx(port: IOdbPort, band: 'rx1310' | 'rx1490'): string {
    const raw = band === 'rx1310' ? port.signal : port.rx_1490;
    const n = parseFloat(String(raw ?? ''));
    return Number.isNaN(n) ? '-' : n.toFixed(2);
  }

  /** Calidad de la señal: la 1310 (Rx en la OLT) frente a los límites de SmartOLT. */
  public severity(port: IOdbPort, band: 'rx1310' | 'rx1490' = 'rx1310'): Severity {
    const n = parseFloat(this.rx(port, band));
    if (Number.isNaN(n)) return 'none';
    const critical = parseFloat(String(port.signal_limit_critical ?? ''));
    const warning = parseFloat(String(port.signal_limit_warning ?? ''));
    if (!Number.isNaN(critical) && n <= critical) return 'critical';
    if (!Number.isNaN(warning) && n <= warning) return 'warning';
    return 'good';
  }

  public signalMeta(port: IOdbPort, band: 'rx1310' | 'rx1490' = 'rx1310') {
    return SIGNAL_META[this.severity(port, band)];
  }

  /** Barras del icono de señal que se pintan (4 = todas). */
  public bars(port: IOdbPort): number {
    return { good: 4, warning: 3, critical: 2, none: 0 }[this.severity(port)];
  }

  public toggleBand(band: 'rx1310' | 'rx1490') {
    this.band[band] = !this.band[band];
    // al menos una banda visible, como en SmartOLT
    if (!this.band.rx1310 && !this.band.rx1490) this.band[band] = true;
  }

  // ---- resumen de estado y señal (pie del modal) ----
  private statusCategory(port: IOdbPort): StatusCategory {
    if (port.free) return 'free';
    if (port.status === 'Online') return 'online';
    if (!port.status || port.status === 'N/A') return 'unknown';
    return 'offline';
  }

  private usageCategory(port: IOdbPort): UsageCategory {
    if (port.free) return 'free';
    const severity = this.severity(port);
    return severity === 'none' ? 'offline' : severity;
  }

  /** Conteos y anillos del pie, con las mismas reglas de SmartOLT. */
  public buildUsage(data: IOdbDetails): IOdbUsage {
    const status: Record<StatusCategory, number> = { online: 0, offline: 0, unknown: 0, free: 0 };
    const signal: Record<UsageCategory, number> = {
      good: 0,
      warning: 0,
      critical: 0,
      offline: 0,
      free: 0,
    };
    (data.ports ?? []).forEach((port) => {
      status[this.statusCategory(port)]++;
      signal[this.usageCategory(port)]++;
    });
    const used = signal.good + signal.warning + signal.critical + signal.offline;
    const defined = data.total_ports_defined !== false && Number(data.nr_of_ports) > 0;
    let free: number | null = null;
    let total = used;
    let utilization = used ? 100 : 0;
    if (defined) {
      const capacity = Number(data.nr_of_ports);
      free = Math.max(0, capacity - used);
      total = Math.max(capacity, used + free);
      utilization = Math.min(100, Math.round((used / capacity) * 100));
    }
    const ringCounts = {
      status: { ...status, free: free ?? 0 },
      signal: { ...signal, free: free ?? 0 },
    };
    return {
      status,
      signal,
      free,
      utilization,
      statusGradient: ringGradient(ringCounts.status, total, {
        online: '#1fb325',
        offline: '#94a3b8',
        unknown: '#fbbf24',
        free: '#64748b',
      }),
      signalGradient: ringGradient(ringCounts.signal, total, {
        good: '#1fb325',
        warning: '#f97316',
        critical: '#ef4444',
        offline: '#cbd5e1',
        free: '#64748b',
      }),
    };
  }

  /** Un clic en un conteo del pie resalta esos puertos; otro clic lo quita. */
  public toggleFilter(type: 'status' | 'signal', value: string) {
    this.filter =
      this.filter?.type === type && this.filter.value === value ? null : { type, value };
  }

  public isFilterOn(type: 'status' | 'signal', value: string): boolean {
    return this.filter?.type === type && this.filter.value === value;
  }

  public dimmed(port: IOdbPort): boolean {
    if (this.pickingOnu) return !port.free;
    if (!this.filter) return false;
    const category =
      this.filter.type === 'status' ? this.statusCategory(port) : this.usageCategory(port);
    return category !== this.filter.value;
  }

  // ---- barra de estado de 24 h ----
  /** Tramos de la barra en % del ancho, con su color y texto. */
  public bandSegments(
    port: IOdbPort,
  ): { left: number; width: number; color: string; title: string }[] {
    const segments: IOnuStatusSegment[] = (port.id && this.series[port.id]?.segments) || [];
    const from = this.seriesFrom * 1000;
    const span = (this.seriesTo - this.seriesFrom) * 1000;
    if (!segments.length || !span) return [];
    const time = (ms: number) =>
      new Date(ms).toLocaleString('es', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    return segments.map((s) => {
      const start = Math.max(s.from, from);
      const end = Math.min(s.to, from + span);
      return {
        left: ((start - from) / span) * 100,
        width: Math.max(0.5, ((end - start) / span) * 100),
        color: STATUS_BAND_COLORS[s.status] ?? '#94a3b8',
        title: `${s.status}: ${time(s.from)} → ${time(s.to)}`,
      };
    });
  }

  // ---- selección ----
  public isSelected(port: IOdbPort): boolean {
    return port.unassigned
      ? String(port.id) === this.selected.onuId
      : Number(port.port) === this.selected.port;
  }

  public select(port: IOdbPort) {
    // en la tarjeta cada ONU abre su detalle, como el enlace de SmartOLT
    if (this.card) {
      if (port.id && !port.free) this.openOnu.emit(String(port.id));
      return;
    }
    // con una ONU esperando puerto, un clic en un puerto vacío la coloca
    if (this.pickingOnu) {
      if (port.free && port.port) this.attach(this.pickingOnu, Number(port.port));
      return;
    }
    this.searchTerm = '';
    this.searchResults = null;
    this.selected = port.unassigned
      ? { onuId: String(port.id), port: null }
      : { onuId: port.id ? String(port.id) : null, port: Number(port.port) || null };
  }

  /** Puerto u ONU que muestra el panel de la derecha. */
  public get current(): IOdbPort | null {
    const ports = this.data?.ports ?? [];
    if (this.selected.port)
      return ports.find((p) => !p.unassigned && Number(p.port) === this.selected.port) ?? null;
    return ports.find((p) => String(p.id) === this.selected.onuId) ?? null;
  }

  public get panelTitle(): string {
    const c = this.current;
    if (!c) return 'Sin selección';
    return Number(c.port) ? this.portLabel(c) : 'Sin puerto';
  }

  /** Distancia en metros, o "-" si la OLT no la informó. */
  public distance(port: IOdbPort): string {
    const meters = parseFloat(String(port.distance ?? ''));
    return Number.isNaN(meters) ? '-' : `${meters} m`;
  }

  public hasMap(port: IOdbPort): boolean {
    const lat = parseFloat(String(port.latitude ?? ''));
    const lng = parseFloat(String(port.longitude ?? ''));
    return (
      Number(port.has_coords) === 1 &&
      !Number.isNaN(lat) &&
      !Number.isNaN(lng) &&
      !(lat === 0 && lng === 0)
    );
  }

  public mapUrl(port: IOdbPort): string {
    return `https://www.google.com/maps?q=${port.latitude},${port.longitude}`;
  }

  /** Menú ⋮ del puerto: las mismas opciones del panel, agrupadas como en SmartOLT. */
  public openMenu(event: Event, port: IOdbPort, menu: Menu) {
    event.stopPropagation();
    this.select(port);
    if (port.free) {
      this.menuItems = [
        {
          label: 'Puerto del splitter',
          items: [{ label: 'Asignar ONU', icon: 'pi pi-plus', command: () => this.focusSearch() }],
        },
      ];
    } else {
      const portItems: MenuItem[] = [];
      if (this.freePorts) {
        portItems.push({
          label: this.portNo(port) ? 'Cambiar de puerto' : 'Asignar puerto',
          icon: 'pi pi-arrow-right-arrow-left',
          command: () => this.startPick(port),
        });
      } else if (!this.portNo(port)) {
        portItems.push({ label: 'Sin puertos vacíos', disabled: true });
      }
      portItems.push({
        label: 'Quitar',
        icon: 'pi pi-minus-circle',
        command: () => this.unassign(port),
      });
      this.menuItems = [
        { label: 'Puerto del splitter', items: portItems },
        {
          label: 'ONU',
          items: [
            {
              label: 'Ver detalle de la ONU',
              icon: 'pi pi-external-link',
              command: () => this.openOnu.emit(port.id!),
            },
            { label: 'Actualizar señal', icon: 'pi pi-refresh', command: () => this.refresh() },
          ],
        },
        {
          label: 'Mapa',
          items: [
            this.hasMap(port)
              ? {
                  label: 'Ver en el mapa',
                  icon: 'pi pi-map-marker',
                  url: this.mapUrl(port),
                  target: '_blank',
                }
              : { label: 'La ONU no tiene ubicación en el mapa', disabled: true },
          ],
        },
      ];
    }
    menu.toggle(event);
  }

  private focusSearch() {
    setTimeout(() => document.querySelector<HTMLInputElement>('#odb-search-onu')?.focus());
  }

  // ---- número de puertos ----
  public startEditPorts() {
    this.editingPorts = true;
    this.portChoice = '';
    this.portOther = null;
  }

  public get portsToSave(): number | null {
    const n = this.portChoice === 'other' ? Number(this.portOther) : Number(this.portChoice);
    return Number.isInteger(n) && n > 0 ? n : null;
  }

  public get portsError(): string {
    const n = this.portsToSave;
    const used = this.data?.used_ports ?? 0;
    if (n === null) return '';
    if (n > 256) return 'Indique un número de puertos entre 1 y 256.';
    if (n < used)
      return `Este splitter tiene ${used} ONUs asignadas: no puede tener menos de ${used} puertos.`;
    return '';
  }

  public savePorts() {
    const n = this.portsToSave;
    if (n === null || this.portsError) return;
    this.confirmationService.confirm({
      header: 'Número de puertos',
      message: `Se definirá el splitter <strong>${this.data?.name}</strong> con <strong>${n} puertos</strong>. Afecta a las ${this.data?.used_ports} ONUs asignadas. ¿Continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, guardar',
      rejectLabel: 'No',
      rejectButtonStyleClass: 'p-button-text',
      accept: () =>
        this.run('odb_set_ports', { nr_of_ports: n }, () => (this.editingPorts = false)),
    });
  }

  // ---- asignar / quitar ----
  public startPick(onu: IOdbPort) {
    if (!this.freePorts) return;
    this.pickingOnu = onu;
  }

  public cancelPick() {
    this.pickingOnu = null;
  }

  public async attach(onu: { id: string | null }, port: number) {
    this.pickingOnu = null;
    await this.run('odb_attach_onu', { onu_id: onu.id, odb_port: port }, () => {
      this.selected = { onuId: String(onu.id), port };
      this.searchResults = null;
      this.searchTerm = '';
    });
  }

  /** Quita la ONU del puerto o del splitter, con la misma pregunta de SmartOLT. */
  public unassign(onu: IOdbPort) {
    const label = onu.name || onu.sn || `#${onu.id}`;
    const hasPort = !!Number(onu.port);
    this.confirmationService.confirm({
      header: 'Quitar ONU',
      message: hasPort
        ? `¿Quitar la ONU <strong>${label}</strong>? Puede dejarla en el splitter sin puerto o sacarla del splitter.`
        : `¿Sacar la ONU <strong>${label}</strong> del splitter?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: hasPort ? 'Solo vaciar el puerto' : 'Sacar del splitter',
      rejectLabel: hasPort ? 'Sacar del splitter' : 'Cancelar',
      rejectButtonStyleClass: hasPort ? 'p-button-danger p-button-outlined' : 'p-button-text',
      accept: () => this.run(hasPort ? 'odb_clear_port' : 'odb_detach_onu', { onu_id: onu.id }),
      reject: (reason?: ConfirmEventType) => {
        // con puerto, el segundo botón es "Sacar del splitter"; cerrar con la X o Esc no hace nada
        if (hasPort && reason === ConfirmEventType.REJECT)
          this.run('odb_detach_onu', { onu_id: onu.id });
      },
    });
  }

  private async run(
    action: 'odb_set_ports' | 'odb_attach_onu' | 'odb_detach_onu' | 'odb_clear_port',
    body: Record<string, unknown>,
    after?: () => void,
  ) {
    this.busy = true;
    try {
      const response = await this.onuViewService.action(this.view.onuId, action, {
        ...body,
        odbId: this.odbId,
      });
      this.toastService.success(response.message || 'Cambio aplicado en SmartOLT');
      after?.();
      await this.load();
      this.changed.emit();
    } catch (error) {
      // el interceptor ya mostró el motivo
    } finally {
      this.busy = false;
    }
  }

  // ---- búsqueda de ONUs para un puerto vacío ----
  public onSearch(term: string) {
    this.searchTerm = term;
    clearTimeout(this.searchTimer);
    if (term.trim().length < 2) {
      this.searchResults = null;
      return;
    }
    this.searchTimer = setTimeout(() => this.search(term.trim()), 350);
  }

  private async search(term: string) {
    this.searching = true;
    try {
      const response = await this.onuViewService.read<{ onus?: IOdbSearchOnu[] }>(
        this.view.onuId,
        'odb_search_onus',
        { odbId: this.odbId, q: term },
      );
      this.searchResults = response.data?.onus ?? [];
    } catch (error) {
      this.searchResults = [];
    } finally {
      this.searching = false;
    }
  }

  // ---- refrescar la señal desde la OLT ----
  private ponRefresh(port: IOdbPort) {
    return this.onuViewService.action(this.view.onuId, 'pon_refresh', {
      oltId: port.olt_id,
      board: port.board,
      ponPort: port.pon_port,
    });
  }

  /** Señal de la ONU seleccionada: se relee su PON en la OLT. */
  public async refresh() {
    const c = this.current;
    if (!c?.olt_id || !c.board || !c.pon_port) return;
    this.refreshing = true;
    try {
      await this.ponRefresh(c);
      await this.load();
      this.toastService.success('Señal actualizada desde la OLT');
    } catch (error) {
      // el interceptor ya mostró el motivo
    } finally {
      this.refreshing = false;
    }
  }

  /** Botón Actualizar del pie: relee cada PON del splitter una vez y vuelve a cargarlo. */
  public async refreshAll() {
    const pons = new Map<string, IOdbPort>();
    (this.data?.ports ?? []).forEach((p) => {
      if (p.olt_id && p.board && p.pon_port) pons.set(`${p.olt_id}/${p.board}/${p.pon_port}`, p);
    });
    this.refreshingAll = true;
    try {
      for (const port of pons.values()) await this.ponRefresh(port);
      await this.load();
      this.toastService.success('Señal actualizada desde la OLT');
    } catch (error) {
      // el interceptor ya mostró el motivo
    } finally {
      this.refreshingAll = false;
    }
  }
}
